import { type KeyboardEvent, type ReactElement, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/ui/components/Icon';

import { ProblemRow } from './ProblemRow';
import type { ProblemRow as ProblemRowModel } from './problemList';
import styles from './ProblemsPanel.module.css';

/**
 * The problems surface — ADR-0101 §7.14, §8, docs/DESIGN.md §6.4, §7.4, docs/ARCHITECTURE.md §3,
 * ADR-0008.
 *
 * It renders rows that `problemList.ts` already built and owns no opinion about ordering,
 * labelling or fixability. What it DOES own is the one thing a list of twenty keyboard targets
 * has to get right: focus.
 *
 * ═══ THE HEAD IS GONE ═══
 *
 * It used to carry a machine label, three count chips and a keyboard hint. ADR-0101 §7.14
 * removes all three, and none of it is lost:
 *
 *   · the TITLE is the drawer's tab, which is also the thing you click to get here;
 *   · the COUNT is the tab's badge, and the BREAKDOWN is the status door in the header — which
 *     is the control that opened this list, so the number is now stated once instead of twice
 *     (ADR-0101 §0 item 3);
 *   · the HINT moved into the desktop footer, where the other two keycaps already were.
 *
 * The `counts` prop went with it. That prop existed so a FILTERED list could still admit a
 * hidden warning, and the drawer's badge does that job now from the same report — which is why
 * the "counts from the REPORT, not from rows.length" test moved to `Drawer.test.tsx` rather
 * than being deleted.
 *
 * ═══ ONE TAB STOP, NOT TWENTY (docs/DESIGN.md §7.4) ═══
 *
 * Roving `tabIndex`: exactly one row is `0` and the rest are `-1`, so Tab crosses the whole
 * list in one press and `↑` / `↓` move within it. Moving selects immediately — "строка
 * активируется сразу" — which is why the arrow handler calls `onSelect` rather than only moving
 * focus. `⏎` is the button's own activation and jumps to the position in the editor.
 *
 * Arrows CLAMP at the ends while `F8` WRAPS, and that difference is deliberate rather than an
 * oversight. Inside a list, arrow keys that teleport from the last row to the first are
 * disorienting (APG's listbox pattern clamps, and `Home` / `End` exist for the ends); `F8` is a
 * global "next problem" command with no visible list under it — and since ADR-0101 §8.2, one
 * that may have to OPEN the list first — where wrapping is the only behaviour that can reach
 * every problem from anywhere.
 *
 * ═══ WHY THE SELECTED INDEX IS A PROP ═══
 *
 * The selection lives in `problemCursor` (see `problemList.ts`), outside React, because three
 * consumers need it: this panel's `aria-current`, the `F8` command, and the reveal into the
 * editor. A `useState` here would let the panel and `F8` disagree about which problem is
 * current — the one thing a problems list may never do.
 */

export interface ProblemsPanelProps {
  /** Already ordered, already localised, already filtered. See `buildProblemList`. */
  readonly rows: readonly ProblemRowModel[];
  /** `ProblemRow.index`, i.e. the position in the UNFILTERED list. `-1` selects nothing. */
  readonly selectedIndex: number;
  readonly onSelect: (row: ProblemRowModel) => void;
  /** Jump to the position in the editor. */
  readonly onActivate: (row: ProblemRowModel) => void;
  readonly onApplyFix?: ((row: ProblemRowModel) => void) | undefined;
  readonly className?: string;
  readonly id?: string;
}

export function ProblemsPanel({
  rows,
  selectedIndex,
  onSelect,
  onActivate,
  onApplyFix,
  className,
  id,
}: ProblemsPanelProps): ReactElement {
  const { t } = useTranslation();

  const buttons = useRef(new Map<string, HTMLButtonElement>());

  const position = rows.findIndex((row) => row.index === selectedIndex);
  const current = position < 0 ? undefined : rows[position];

  // Roving focus needs a home when nothing is selected yet, or the list would be unreachable
  // by Tab entirely (every row `-1` is a list with no tab stop).
  const tabStopId = current?.id ?? rows[0]?.id;

  const focusRow = useCallback((row: ProblemRowModel | undefined): void => {
    if (row === undefined) return;
    buttons.current.get(row.id)?.focus();
  }, []);

  const move = useCallback(
    (to: number): void => {
      const target = rows[to];
      if (target === undefined) return;
      onSelect(target);
      focusRow(target);
    },
    [focusRow, onSelect, rows],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>): void => {
      const from = position < 0 ? -1 : position;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          move(Math.min(from + 1, rows.length - 1));
          return;
        case 'ArrowUp':
          event.preventDefault();
          // From "nothing selected", ↑ lands on the first row rather than the last: the
          // focus is already in the list, so the user is stepping within what they see.
          move(from <= 0 ? 0 : from - 1);
          return;
        case 'Home':
          event.preventDefault();
          move(0);
          return;
        case 'End':
          event.preventDefault();
          move(rows.length - 1);
          return;
        default:
      }
    },
    [move, position, rows.length],
  );

  // The selection also moves from OUTSIDE this panel — `F8` steps it while focus is in the
  // editor. Scrolling is all that is owed there: stealing focus back into the list would
  // undo the jump the user just asked for (docs/DESIGN.md §7.4, F8 = "перенос фокуса и
  // скролл к позиции" — to the position, not to the row).
  useEffect(() => {
    if (current === undefined) return;
    buttons.current.get(current.id)?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const severityLabel = {
    error: t('editor.problems.severityError'),
    warning: t('editor.problems.severityWarning'),
    info: t('editor.problems.severityNote'),
  } as const;

  const fixLabel = t('action.fix');

  return (
    <section
      aria-label={t('a11y.problemsPanel')}
      className={[styles.panel, className].filter(Boolean).join(' ')}
      // The forced-colors rule in `ui/styles/base.css` keys off this attribute: without it
      // the panel, which is separated by a tone step alone, merges into the page under
      // Windows High Contrast (docs/DESIGN.md §4.5).
      data-surface="panel"
      id={id}
    >
      {rows.length === 0 ? (
        <p className={styles.empty}>
          {/* The one place in this component that is green: "nothing is wrong" is a verdict,
              and lime is the family that carries it everywhere else (ADR-0101 §1). */}
          <span aria-hidden="true" className={styles.emptyDisc}>
            <Icon name="check" size={15} />
          </span>
          {t('editor.problems.empty')}
        </p>
      ) : (
        <ul
          className={styles.list}
          // The dense-list focus ring: `outline-offset: -2px`, so the ring draws inward and
          // is not clipped by the neighbouring row (docs/DESIGN.md §7.2).
          data-dense-focus=""
          onKeyDown={onKeyDown}
          role="list"
        >
          {rows.map((row) => (
            <ProblemRow
              current={row.index === selectedIndex}
              fixLabel={fixLabel}
              key={row.id}
              onActivate={() => {
                onActivate(row);
              }}
              onApplyFix={
                onApplyFix === undefined
                  ? undefined
                  : () => {
                      onApplyFix(row);
                    }
              }
              onSelect={() => {
                onSelect(row);
              }}
              positionLabel={t('editor.problems.position', {
                line: row.line,
                column: row.column,
              })}
              ref={(element) => {
                if (element === null) buttons.current.delete(row.id);
                else buttons.current.set(row.id, element);
              }}
              row={row}
              severityLabel={severityLabel[row.severity]}
              tabIndex={row.id === tabStopId ? 0 : -1}
            />
          ))}
        </ul>
      )}

      {/*
        The keyboard contract, stated where the keyboard is. Hidden below 960px by CSS: a keycap
        hint is dead weight on a touch device, and those pixels belong to the diagnostics.

        `F8` joined the other two here when the panel's head was removed (ADR-0101 §7.14). It is
        the one key in the row that does something the list cannot do on its own — it steps the
        selection from ANYWHERE, including the editor, and opens this drawer if it is shut.

        `⌥E errors only` USED TO BE A FOURTH ROW, and nothing was bound to it. The footer is a
        keyboard contract, and two thirds of it were honoured: ⏎ jumps, ↑↓ move. The third
        promised a filter that did not exist anywhere in the product — no binding (`grep "Alt-"`
        over `src/` returns nothing and `buildKeymap` derives its keys only from
        `Command.shortcut`), no toggle, and no state to toggle. A user who pressed ⌥E on macOS
        did not get a filter; they armed an acute-accent dead key inside the editor's
        contenteditable. It was deleted rather than wired, and `ProblemListOptions.filter` is
        kept: it is two lines, it is unit-tested, and it is what the toggle will use.
      */}
      <div className={styles.foot}>
        <span>
          <kbd>⏎</kbd> {t('editor.problems.keyJump')}
        </span>
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd> {t('editor.problems.keyMove')}
        </span>
        <span>
          <kbd>F8</kbd> {t('editor.problems.keyNext')}
        </span>
      </div>
    </section>
  );
}

export default ProblemsPanel;
